import React, { useCallback, useEffect, useState } from 'react';
import {
  FileText,
  Download,
  Pencil,
  Trash2,
  CheckCircle2,
  Check,
  Clock,
  Building2,
  Inbox,
  Loader2,
  AlertTriangle,
  Upload,
  Scale,
  Keyboard,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/toaster.jsx';
import { cn } from '@/lib/utils';
import {
  listLclCotizaciones,
  deleteLclCotizacion,
  selectLclCotizacion,
  setEstadoLclCotizacion,
  getLclCotizacionPdfUrl,
} from '../lclCotizaciones.service.js';
import { formatImporte } from '../utils/lclCotizacionCalc.js';
import CargarLclCotizacionDialog from './CargarLclCotizacionDialog.jsx';
import ComparadorCotizacionesDialog from './ComparadorCotizacionesDialog.jsx';

const ESTADO_BADGE = {
  'Pendiente de revisión': 'bg-amber-100 text-amber-800 border-amber-200',
  Confirmada: 'bg-blue-100 text-blue-800 border-blue-200',
  Seleccionada: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  Descartada: 'bg-rose-100 text-rose-800 border-rose-200',
};

const formatFecha = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  } catch {
    return '—';
  }
};

/** Días que faltan para que venza la oferta; negativo = vencida. */
const diasParaVencer = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
};

/**
 * Cotizaciones recibidas para una solicitud LCL.
 *
 * Tiene sus propios hooks a propósito: el diálogo de detalle que la
 * monta hace `if (!request) return null` antes de cualquier hook, así
 * que no puede tener estado propio.
 */
export default function LclCotizacionesProveedoresSection({
  lclRequestId,
  lclRequest,
  onChanged,
}) {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [aBorrar, setABorrar] = useState(null);
  const [editando, setEditando] = useState(null);
  const [cargarAbierto, setCargarAbierto] = useState(false);
  const [comparadorAbierto, setComparadorAbierto] = useState(false);

  const cargar = useCallback(async () => {
    if (!lclRequestId) return;
    setCargando(true);
    try {
      const r = await listLclCotizaciones(lclRequestId);
      setItems(r?.data || []);
    } catch (e) {
      toast.error('No se pudieron cargar las cotizaciones', e?.message);
    } finally {
      setCargando(false);
    }
  }, [lclRequestId, toast]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const seleccionar = async (c) => {
    const r = await selectLclCotizacion(c._id);
    if (r?.success) {
      toast.success('Cotización seleccionada', c.numero);
      cargar();
      onChanged?.();
    } else {
      toast.error('No se pudo seleccionar', r?.message);
    }
  };

  const confirmar = async (c) => {
    const r = await setEstadoLclCotizacion(c._id, 'Confirmada');
    if (r?.success) {
      toast.success('Cotización confirmada', c.numero);
      cargar();
    } else {
      toast.error('No se pudo confirmar', r?.message);
    }
  };

  const descargar = async (c) => {
    const r = await getLclCotizacionPdfUrl(c._id);
    if (r?.success && r.data?.url) window.open(r.data.url, '_blank', 'noopener');
    else toast.error('No se pudo abrir el documento', r?.message);
  };

  const borrar = async () => {
    if (!aBorrar) return;
    const r = await deleteLclCotizacion(aBorrar._id);
    if (r?.success) {
      toast.success('Cotización eliminada', aBorrar.numero);
      cargar();
      onChanged?.();
    } else {
      toast.error('No se pudo eliminar', r?.message);
    }
    setABorrar(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {items.length === 0
            ? 'Sin cotizaciones cargadas'
            : `${items.length} cotización(es) recibida(s)`}
        </p>
        <div className="flex gap-2">
          {items.length > 1 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setComparadorAbierto(true)}
            >
              <Scale className="mr-2 h-4 w-4" /> Comparar
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setCargarAbierto(true)}>
            <Upload className="mr-2 h-4 w-4" /> Cargar cotización
          </Button>
        </div>
      </div>

      {cargando && (
        <div className="flex items-center justify-center py-6 text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Cargando…
        </div>
      )}

      {!cargando && items.length === 0 && (
        <div className="flex flex-col items-center rounded-lg border border-dashed py-8 text-center">
          <Inbox className="mb-2 h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Todavía no cargaste ninguna cotización de proveedor.
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Podés subir el PDF que mandaron o cargarla a mano si contestaron
            por correo o por teléfono.
          </p>
        </div>
      )}

      <div className="space-y-2">
        {items.map((c) => {
          const seleccionada = c.estado === 'Seleccionada';
          const dias = diasParaVencer(c.vigenciaHasta);
          return (
            <div
              key={c._id}
              className={cn(
                'rounded-lg border p-3 transition-colors',
                seleccionada && 'border-emerald-500 ring-1 ring-emerald-200',
              )}
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{c.numero}</span>
                    <Badge
                      variant="outline"
                      className={cn('font-normal', ESTADO_BADGE[c.estado])}
                    >
                      {seleccionada && <Check className="mr-1 h-3 w-3" />}
                      {c.estado}
                    </Badge>
                    {!c.documento?.r2Key && (
                      <Badge
                        variant="outline"
                        className="font-normal text-muted-foreground"
                        title="Cargada a mano: no hay documento del proveedor"
                      >
                        <Keyboard className="mr-1 h-3 w-3" />
                        manual
                      </Badge>
                    )}
                    {c.totalIncompleto && (
                      <Badge
                        variant="outline"
                        className="border-amber-300 bg-amber-50 font-normal text-amber-800"
                      >
                        <AlertTriangle className="mr-1 h-3 w-3" />
                        falta TRM ({(c.monedasSinTasa || []).join(', ')})
                      </Badge>
                    )}
                    {dias !== null && dias < 0 && (
                      <Badge
                        variant="outline"
                        className="border-rose-300 bg-rose-50 font-normal text-rose-800"
                      >
                        vencida
                      </Badge>
                    )}
                    {dias !== null && dias >= 0 && dias <= 5 && (
                      <Badge
                        variant="outline"
                        className="border-amber-300 bg-amber-50 font-normal text-amber-800"
                      >
                        vence en {dias} d
                      </Badge>
                    )}
                  </div>

                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Building2 className="h-3 w-3" />
                      {c.proveedorSnapshot?.nombre || 'Proveedor sin identificar'}
                    </span>
                    {c.diasTransito != null && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {c.diasTransito} días
                      </span>
                    )}
                    <span>Vigencia: {formatFecha(c.vigenciaHasta)}</span>
                    <span>{(c.conceptos || []).length} conceptos</span>
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-lg font-semibold">
                    {formatImporte(c.total, c.moneda)}
                  </div>
                  {c.trm && (
                    <div className="text-[11px] text-muted-foreground">
                      TRM {c.trm}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-2 flex flex-wrap gap-1.5">
                {!seleccionada && c.estado !== 'Descartada' && (
                  <Button size="sm" onClick={() => seleccionar(c)}>
                    <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Seleccionar
                  </Button>
                )}
                {c.estado === 'Pendiente de revisión' && (
                  <Button size="sm" variant="outline" onClick={() => confirmar(c)}>
                    Confirmar
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setEditando(c)}>
                  <Pencil className="mr-1.5 h-3.5 w-3.5" /> Editar
                </Button>
                {c.documento?.r2Key && (
                  <Button size="sm" variant="outline" onClick={() => descargar(c)}>
                    <Download className="mr-1.5 h-3.5 w-3.5" /> Documento
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => setABorrar(c)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {(cargarAbierto || editando) && (
        <CargarLclCotizacionDialog
          open
          onOpenChange={(v) => {
            if (!v) {
              setCargarAbierto(false);
              setEditando(null);
            }
          }}
          lclRequest={lclRequest || { _id: lclRequestId }}
          cotizacion={editando}
          onSaved={() => {
            cargar();
            onChanged?.();
          }}
        />
      )}

      {comparadorAbierto && (
        <ComparadorCotizacionesDialog
          open
          onOpenChange={(v) => !v && setComparadorAbierto(false)}
          lclRequest={lclRequest || { _id: lclRequestId }}
          onChanged={() => {
            cargar();
            onChanged?.();
          }}
        />
      )}

      <AlertDialog open={!!aBorrar} onOpenChange={(v) => !v && setABorrar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar la cotización?</AlertDialogTitle>
            <AlertDialogDescription>
              {aBorrar && (
                <>
                  Se elimina <strong>{aBorrar.numero}</strong> de{' '}
                  {aBorrar.proveedorSnapshot?.nombre || 'el proveedor'}, junto con el
                  documento original. No se puede deshacer.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={borrar}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
